/**
 * PublicVisitorLivePOBPage — shown when a visitor scans their own badge QR.
 * URL: https://<app>/?vis=<visCardQrHash>
 * No login. Visitor is identified from the QR hash. They report SAFE or NEED HELP.
 */
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Stethoscope, Flame, Lock, HelpCircle, Shield, MoreHorizontal, FileWarning, ArrowLeft } from 'lucide-react';
import IncidentReportForm from '../components/IncidentReportForm';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const S = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', orange: '#f97316', pink: '#ec4899', purple: '#a78bfa',
  teal: '#14b8a6',
};

export default function PublicVisitorLivePOBPage({ visHash }) {
  const [phase, setPhase] = useState('loading'); // loading | action | emergency | other | done | error
  const [otherFunction, setOtherFunction] = useState(null); // null | 'incident'
  const [card, setCard] = useState(null);
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/public/visitor-card/${visHash}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setPhase('error'); return; }
        setCard(data);
        setPhase('action');
        if (navigator.geolocation) {
          setGpsLoading(true);
          navigator.geolocation.getCurrentPosition(
            pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
            () => setGpsLoading(false),
            { timeout: 8000 }
          );
        }
      })
      .catch(() => { setError('Unable to reach the server. Check your connection.'); setPhase('error'); });
  }, [visHash]);

  function handleSafe() {
    submit('SAFE', null);
  }

  function handleEmergencyType(type) {
    submit('NEED_HELP', type);
  }

  function submit(action, emergencyType) {
    setSubmitting(true);
    fetch(`${API}/api/public/visitor-scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visCardQrHash: visHash, action, emergencyType: emergencyType || undefined, gps: gps || undefined }),
    })
      .then(r => r.json())
      .then(data => {
        setSubmitting(false);
        if (data.error) { setError(data.error); return; }
        setResult({ ...data, action });
        setPhase('done');
      })
      .catch(() => { setSubmitting(false); setError('Network error. Please try again.'); });
  }

  const page = {
    minHeight: '100vh', background: S.bg, display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '0 0 40px',
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  };
  const header = {
    width: '100%', background: S.surface, borderBottom: `1px solid ${S.border}`,
    padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px',
  };
  const pageCard = { width: '100%', maxWidth: '420px', padding: '0 20px' };

  if (phase === 'loading') return (
    <div style={page}>
      <div style={{ marginTop: '40vh', color: S.muted, fontSize: '14px', textAlign: 'center' }}>
        <Shield size={32} color={S.blue} style={{ display: 'block', margin: '0 auto 12px' }} />
        Loading…
      </div>
    </div>
  );

  if (phase === 'error') return (
    <div style={page}>
      <div style={{ marginTop: '30vh', padding: '32px', textAlign: 'center', maxWidth: '360px' }}>
        <AlertTriangle size={40} color={S.amber} style={{ display: 'block', margin: '0 auto 16px' }} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: S.text, marginBottom: '8px' }}>Error</div>
        <div style={{ fontSize: '14px', color: S.subtle }}>{error}</div>
      </div>
    </div>
  );

  const displayName = card?.visitorName || card?.cardNumber;

  return (
    <div style={page}>
      <div style={header}>
        <Shield size={20} color={S.blue} />
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: S.text }}>SafeCheck</div>
          <div style={{ fontSize: '11px', color: S.muted }}>LivePOB — Visitor Status</div>
        </div>
        {gpsLoading && <div style={{ marginLeft: 'auto', fontSize: '11px', color: S.blue }}>📍 Getting GPS…</div>}
        {gps && !gpsLoading && <div style={{ marginLeft: 'auto', fontSize: '11px', color: S.green }}>📍 GPS ready</div>}
      </div>

      <div style={pageCard}>
        {/* Visitor greeting */}
        {card && (
          <div style={{ background: S.surface, border: `1px solid ${S.border}`, borderRadius: '14px', padding: '16px 18px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '12px', color: S.muted, marginBottom: '4px' }}>Hello,</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: S.text, marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
              <div style={{ fontSize: '12px', color: S.subtle }}>{card.cardNumber} · {card.siteName}</div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '10px', background: S.elevated, border: `2px solid ${S.teal}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '11px', fontWeight: 700, color: S.teal, textAlign: 'center', lineHeight: 1.2 }}>
              VISITOR
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: S.red }}>
            {error}
          </div>
        )}

        {/* Action phase */}
        {phase === 'action' && (
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: S.text, marginBottom: '20px', textAlign: 'center' }}>
              What is your current status?
            </div>

            <button
              onClick={handleSafe}
              disabled={submitting}
              style={{ width: '100%', padding: '24px 16px', borderRadius: '16px', border: 'none', background: S.green, color: '#fff', fontSize: '20px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginBottom: '14px', boxShadow: `0 6px 24px ${S.green}44` }}
            >
              <CheckCircle size={30} />
              I AM SAFE
            </button>

            <button
              onClick={() => setPhase('emergency')}
              disabled={submitting}
              style={{ width: '100%', padding: '24px 16px', borderRadius: '16px', border: 'none', background: S.red, color: '#fff', fontSize: '20px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', boxShadow: `0 6px 24px ${S.red}44` }}
            >
              <AlertTriangle size={30} />
              I NEED HELP
            </button>

            {card?.incidentReportingEnabled && (
              <button
                onClick={() => { setOtherFunction(null); setPhase('other'); }}
                disabled={submitting}
                style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '14px' }}
              >
                <MoreHorizontal size={22} color={S.subtle} />
                OTHER
              </button>
            )}

            {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '14px' }}>Submitting…</div>}
          </div>
        )}

        {/* Other phase — visitor secondary functions (Report Incident only for now) */}
        {phase === 'other' && (
          <div>
            <button onClick={() => {
                if (otherFunction === 'incident') { setOtherFunction(null); return; }
                setPhase('action');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: S.muted, fontSize: '14px', cursor: 'pointer', padding: '4px 0', marginBottom: '14px', fontFamily: 'inherit' }}>
              <ArrowLeft size={18} />
              Back
            </button>

            {otherFunction === 'incident' ? (
              <IncidentReportForm
                apiBase={API}
                visCardHash={visHash}
                gps={gps}
                onSubmitted={() => { /* form shows its own success view */ }}
                onBack={() => setOtherFunction(null)}
                S={S}
              />
            ) : (
              card?.incidentReportingEnabled && (
                <button onClick={() => setOtherFunction('incident')}
                  style={{ width: '100%', padding: '18px 16px', borderRadius: '14px', border: `1px solid ${S.border}`, background: S.surface, color: S.text, fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileWarning size={22} color={S.amber} />
                  <div style={{ textAlign: 'left' }}>
                    <div>Report Incident</div>
                    <div style={{ fontSize: '11px', color: S.muted, marginTop: '2px' }}>Injury, near-miss, property damage…</div>
                  </div>
                </button>
              )
            )}
          </div>
        )}

        {/* Emergency type selection */}
        {phase === 'emergency' && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <AlertTriangle size={48} color={S.red} style={{ display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '22px', fontWeight: 700, color: S.red, marginBottom: '6px' }}>Emergency Alert</div>
              <div style={{ fontSize: '14px', color: S.subtle }}>Select the type of emergency</div>
              {gpsLoading && <div style={{ fontSize: '12px', color: S.blue, marginTop: '8px' }}>Capturing your GPS location…</div>}
              {gps && <div style={{ fontSize: '11px', color: S.green, marginTop: '6px', fontFamily: 'JetBrains Mono, monospace' }}>GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                ['MEDICAL', 'Medical', S.pink,   Stethoscope],
                ['FIRE',    'Fire',    S.orange,  Flame],
                ['SECURITY','Security',S.purple,  Lock],
                ['OTHER',   'Other',   S.amber,   HelpCircle],
              ].map(([type, label, color, Icon]) => (
                <button key={type} onClick={() => handleEmergencyType(type)} disabled={submitting}
                  style={{ padding: '26px 10px', borderRadius: '16px', border: `2px solid ${color}`, background: color+'22', color, fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <Icon size={32} />
                  {label}
                </button>
              ))}
            </div>

            <button onClick={() => setPhase('action')} style={{ width: '100%', marginTop: '16px', padding: '12px', borderRadius: '10px', border: `1px solid ${S.border}`, background: 'transparent', color: S.muted, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
              ← Back
            </button>

            {submitting && <div style={{ textAlign: 'center', color: S.muted, fontSize: '13px', marginTop: '14px' }}>Sending alert…</div>}
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <CheckCircle size={72} color={result?.action === 'NEED_HELP' ? S.red : S.green} style={{ display: 'block', margin: '0 auto 20px' }} />
            <div style={{ fontSize: '24px', fontWeight: 700, color: S.text, marginBottom: '10px' }}>
              {result?.action === 'NEED_HELP' ? '🚨 Alert Sent' : '✓ Marked Safe'}
            </div>
            <div style={{ fontSize: '15px', color: S.subtle, marginBottom: '6px' }}>{result?.visitorName || result?.cardNumber}</div>

            {result?.action === 'NEED_HELP' && (
              <div style={{ background: S.red+'22', border: `1px solid ${S.red}44`, borderRadius: '12px', padding: '16px', margin: '20px 0', fontSize: '14px', color: S.red, fontWeight: 600, lineHeight: 1.5 }}>
                Your emergency alert has been sent to the safety team.<br />Stay in place if possible and await assistance.
              </div>
            )}

            {result?.action === 'SAFE' && (
              <div style={{ background: S.green+'22', border: `1px solid ${S.green}44`, borderRadius: '12px', padding: '16px', margin: '20px 0', fontSize: '14px', color: S.green, fontWeight: 600 }}>
                Your status has been recorded. Stay safe.
              </div>
            )}

            <button onClick={() => { setPhase('action'); setResult(null); setError(''); }}
              style={{ padding: '14px 36px', borderRadius: '10px', border: `1px solid ${S.border}`, background: S.elevated, color: S.subtle, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Report Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
